import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Alert, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { APP_NAME } from '../../config/branding';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';

export default function DeleteAccount() {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Récupérer l'email de l'utilisateur depuis le token
  const getUserEmail = () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        return decoded.email;
      } catch (error) {
        console.error('Erreur de décodage du token:', error);
        return null;
      }
    }
    return null;
  };

  const userEmail = getUserEmail();

  const handleDeleteRequest = async () => {
    if (!email || !password) {
      setError(t('delete_account.errors.fill_fields'));
      return;
    }

    // Si l'utilisateur est connecté, vérifier que l'email correspond
    if (userEmail && email !== userEmail) {
      setError(t('delete_account.errors.email_mismatch'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Préparer les headers (avec ou sans token selon si l'utilisateur est connecté)
      const token = localStorage.getItem('access_token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Appel à l'API backend pour supprimer le compte
      const response = await axios.delete('/api/auth/delete-account/', {
        headers: headers,
        data: {
          email: email,
          password: password
        }
      });

      if (response.status === 200) {
        setSuccess(true);
        // Supprimer les tokens locaux
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        // Rediriger vers la page d'accueil après 3 secondes
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError(t('delete_account.errors.must_be_logged_in'));
      } else if (err.response?.status === 400) {
        setError(err.response?.data?.message || t('delete_account.errors.cannot_delete'));
      } else {
        setError(t('delete_account.errors.general_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, bgcolor: 'background.paper' }}>
          <Stack spacing={3} sx={{ mb: 4 }}>
            <Chip color="success" label={t('delete_account.success.deleted')} sx={{ alignSelf: 'flex-start' }} />
            <Typography variant="h4" component="h1">
              {t('delete_account.success.title')}
            </Typography>
          </Stack>
          
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('delete_account.success.data_deleted_notice')}
          </Alert>
          
          <Typography variant="body1" paragraph>
            {t('delete_account.success.redirect_notice')}
          </Typography>
          
          <Button 
            variant="contained" 
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            {t('delete_account.success.back_home')}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      <Stack spacing={3} sx={{ mb: 4 }}>
        <Chip color="error" label={t('delete_account.title')} sx={{ alignSelf: 'flex-start' }} />
        <Typography variant="h3" component="h1">
          {t('delete_account.page_title', { appName: APP_NAME })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('delete_account.irreversible_action')}
        </Typography>
      </Stack>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '320px 1fr' }, gap: { xs: 2, md: 4 } }}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 88 }, alignSelf: 'start' }}>
            <Typography variant="overline" color="text.secondary">{t('delete_account.summary')}</Typography>
            <List dense>
              <ListItem component="a" href="#consequences" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('delete_account.sections.consequences')} />
              </ListItem>
              <ListItem component="a" href="#donnees" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('delete_account.sections.data_deleted')} />
              </ListItem>
              <ListItem component="a" href="#procedure" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('delete_account.sections.procedure')} />
              </ListItem>
              <ListItem component="a" href="#alternatives" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('delete_account.sections.alternatives')} />
              </ListItem>
              <ListItem component="a" href="#droits" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('delete_account.sections.gdpr_rights')} />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Alert severity="warning" sx={{ mb: 4 }}>
              <Typography variant="body2" fontWeight="bold">
                {t('delete_account.warning.attention')}
              </Typography>
            </Alert>

            <Box id="consequences" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('delete_account.sections.consequences')}</Typography>
              <Typography variant="body1" paragraph>
                {t('delete_account.consequences.intro')}
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.consequences.lose_access')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.consequences.data_removed')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.consequences.history_lost')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.consequences.bookings_cancelled')} />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="donnees" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('delete_account.sections.data_deleted')}</Typography>
              <Typography variant="body1" paragraph>
                {t('delete_account.data_deleted.intro')}
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.data_deleted.profile')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.data_deleted.booking_history')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.data_deleted.preferences')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.data_deleted.connection_data')} />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="procedure" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('delete_account.sections.procedure')}</Typography>
              <Typography variant="body1" paragraph>
                {t('delete_account.procedure.intro')}
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.procedure.step1')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.procedure.step2')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.procedure.step3')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.procedure.step4')} />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="alternatives" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('delete_account.sections.alternatives')}</Typography>
              <Typography variant="body1" paragraph>
                {t('delete_account.alternatives.intro')}
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.alternatives.temporarily_disable')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.alternatives.unsubscribe_marketing')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.alternatives.privacy_settings')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.alternatives.contact_support')} />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="droits" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('delete_account.sections.gdpr_rights')}</Typography>
              <Typography variant="body1" paragraph>
                {t('delete_account.gdpr_rights.intro')}
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.gdpr_rights.access')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.gdpr_rights.rectification')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.gdpr_rights.erasure')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.gdpr_rights.limitation')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.gdpr_rights.portability')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('delete_account.gdpr_rights.objection')} />
                </ListItem>
              </List>
              <Typography variant="body1" paragraph>
                {t('delete_account.gdpr_rights.contact_notice')}
              </Typography>
            </Box>

            <Box sx={{ mt: 4, p: 3, bgcolor: 'error.light', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom color="error.dark">
                {t('delete_account.ready_to_delete.title')}
              </Typography>
              <Typography variant="body2" color="error.dark" sx={{ mb: 3 }}>
                {t('delete_account.ready_to_delete.notice')}
              </Typography>
              
              <Button 
                variant="contained" 
                color="error" 
                size="large"
                onClick={() => setOpenDialog(true)}
              >
                {t('delete_account.ready_to_delete.button')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('delete_account.dialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {t('delete_account.dialog.confirm_message')}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('delete_account.dialog.identity_notice')}
          </Typography>
          
          <TextField
            fullWidth
            label={t('delete_account.dialog.email_label')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('delete_account.dialog.email_placeholder')}
            variant="outlined"
            margin="normal"
            autoComplete="email"
          />
          
          <TextField
            fullWidth
            label={t('delete_account.dialog.password_label')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('delete_account.dialog.password_placeholder')}
            variant="outlined"
            margin="normal"
            autoComplete="current-password"
          />
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDialog(false);
            setEmail('');
            setPassword('');
            setError('');
          }}>
            {t('delete_account.dialog.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteRequest}
            color="error"
            variant="contained"
            disabled={loading || !email || !password}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? t('delete_account.dialog.deleting') : t('delete_account.dialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
